import { List, Record } from 'immutable';
import { tFunctionType } from 'app/services/localization';

export * from './main';
export * from './constants';

// By default, we request all abilities that we can from the server.
// While we get all of them in scopes, the server still checks if we
// do actually have those abilities.
//
// Privileged abilities are not requested by default. This needs a
// reauthentication to use. See ElevatedPermissions for that. These
// abilities also only last for 10 minutes before expiring.
//
// Universal abilities are abilities that are granted to all users.
// These abilities do not show up in the user's ability list.

import {
  Ability,
  Subject,
  ABILITIES,
} from './main';

import {
  ADMIN_BOT_BOOKINGS_ABILITY,
  ADMIN_MEETING_BOOKINGS_ABILITY,
  ANSWER_SOCIAL_QUESTION_ABILITY,
  AUTHORIZE_EDITORS_ABILITY,
  AUTHORIZE_MANAGERS_ABILITY,
  AUTHORIZE_SITE_CONTENT_ABILITY,
  AUTHORIZE_TEACHERS_ABILITY,
  AUTOMATIC_CALL_RECORDING_ABILITY,
  CREATE_OTHER_USERS_ABILITY,
  DELETE_USERS_ABILITY,
  DO_RTC_CUSTOMER_SUPPORT_ABILITY,
  EDIT_BANNER_ABILITY,
  EDIT_CATEGORIES_ABILITY,
  EDIT_COMMON_TEXT_ABILITY,
  EDIT_DIALOG_SPEAKER_ABILITY,
  EDIT_DICTIONARY_CONTENT_ABILITY,
  EDIT_LESSON_ABILITY,
  EDIT_LESSON_BOOKINGS_ABILITY,
  VIEW_LESSON_BOOKING_TRANSCRIPTS_ABILITY,
  EDIT_LESSON_BOOKING_RECORDINGS_ABILITY,
  EDIT_LESSON_TRANSLATION_ABILITY,
  EDIT_SITE_STRINGS_ABILITY,
  EDIT_SOCIAL_QUESTION_ABILITY,
  EDIT_TEACHERS_ABILITY,
  EDIT_TEMPLATE_ABILITY,
  EDIT_USERS_ABILITY,
  EDIT_USER_BRAND_ABILITY,
  MANAGE_STAFF_ABILITY,
  PUBLISH_LESSON_ABILITY,
  REPORT_ISSUE_ABILITY,
  TEACH_LESSON_ABILITY,
  TRANSLATE_DICTIONARY_CONTENT_ABILITY,
  USE_ALPHA_FEATURES_ABILITY,
  USE_RESERVED_FEATURES_ABILITY,
  VIEW_LESSON_BOOKINGS_ABILITY,
  VIEW_LESSON_BOOKING_RECORDINGS_ABILITY,
  VIEW_MEETING_BOOKING_RECORDINGS_ABILITY,
  VIEW_SERVER_STATS_ABILITY,
  RETRIEVE_RAW_RTC_PERSISTENCE_ABILITY,
  EDIT_ROOM_PERSISTENCE_ABILITY,
  VIEW_TEACHERS_ABILITY,
  VIEW_USERS_ABILITY,
  EDIT_ORGANIZATIONS_ABILITY,
  VIEW_ORGANIZATIONS_ABILITY,
  DELETE_ORGANIZATIONS_ABILITY,
  CREATE_ORGANIZATIONS_ABILITY,
  EDIT_BLOCKED_EMAIL_DOMAINS_ABILITY,
} from './constants';

export class AbilityGroup extends Record<{
  id: string,
  getName: (t: tFunctionType) => string,
  abilities: List<Ability>,
}>({
  id: '',
  getName: (t) => '',
  abilities: List<Ability>(),
}, 'AbilityGroup') {}

export const ADMIN_ABILITY_GROUP = new AbilityGroup({
  id: 'admin',
  getName: (t) => t('admin.users.ability-groups.admin'),
  abilities: List([
    USE_RESERVED_FEATURES_ABILITY,
    AUTHORIZE_MANAGERS_ABILITY,
    EDIT_BLOCKED_EMAIL_DOMAINS_ABILITY,
    EDIT_USERS_ABILITY,
    EDIT_USER_BRAND_ABILITY,
    VIEW_USERS_ABILITY,
    DELETE_USERS_ABILITY,
    CREATE_OTHER_USERS_ABILITY,
    MANAGE_STAFF_ABILITY,
    EDIT_TEACHERS_ABILITY,
    VIEW_TEACHERS_ABILITY,
    AUTHORIZE_EDITORS_ABILITY,
    AUTHORIZE_TEACHERS_ABILITY,
    AUTHORIZE_SITE_CONTENT_ABILITY,
    VIEW_LESSON_BOOKINGS_ABILITY,
    VIEW_LESSON_BOOKING_TRANSCRIPTS_ABILITY,
    VIEW_LESSON_BOOKING_RECORDINGS_ABILITY,
    EDIT_LESSON_BOOKINGS_ABILITY,
    EDIT_LESSON_BOOKING_RECORDINGS_ABILITY,
    ADMIN_MEETING_BOOKINGS_ABILITY,
    VIEW_MEETING_BOOKING_RECORDINGS_ABILITY,
    ADMIN_BOT_BOOKINGS_ABILITY,
    VIEW_SERVER_STATS_ABILITY,
    RETRIEVE_RAW_RTC_PERSISTENCE_ABILITY,
    EDIT_ROOM_PERSISTENCE_ABILITY,
    EDIT_ORGANIZATIONS_ABILITY,
    VIEW_ORGANIZATIONS_ABILITY,
    DELETE_ORGANIZATIONS_ABILITY,
    CREATE_ORGANIZATIONS_ABILITY,
  ]),
});

export const MATERIALS_ABILITY_GROUP = new AbilityGroup({
  id: 'materials',
  getName: (t) => t('admin.users.ability-groups.materials'),
  abilities: List([
    EDIT_LESSON_ABILITY,
    EDIT_LESSON_TRANSLATION_ABILITY,
    PUBLISH_LESSON_ABILITY,
    EDIT_TEMPLATE_ABILITY,
    EDIT_CATEGORIES_ABILITY,
    EDIT_COMMON_TEXT_ABILITY,
    EDIT_DIALOG_SPEAKER_ABILITY,
  ]),
});

export const DICTIONARY_ABILITY_GROUP = new AbilityGroup({
  id: 'dictionary',
  getName: (t) => t('admin.users.ability-groups.dictionary'),
  abilities: List([
    EDIT_DICTIONARY_CONTENT_ABILITY,
    TRANSLATE_DICTIONARY_CONTENT_ABILITY,
  ]),
});

export const TEACHING_ABILITY_GROUP = new AbilityGroup({
  id: 'teaching',
  getName: (t) => t('admin.users.ability-groups.teaching'),
  abilities: List([
    TEACH_LESSON_ABILITY,
    REPORT_ISSUE_ABILITY,
    AUTOMATIC_CALL_RECORDING_ABILITY,
  ]),
});

export const SITE_CONTENT_ABILITY_GROUP = new AbilityGroup({
  id: 'site-content',
  getName: (t) => t('admin.users.ability-groups.site-content'),
  abilities: List([
    EDIT_SITE_STRINGS_ABILITY,
    EDIT_BANNER_ABILITY,
  ]),
});

export const TESTING_ABILITY_GROUP = new AbilityGroup({
  id: 'testing',
  getName: (t) => t('admin.users.ability-groups.testing'),
  abilities: List([
    USE_ALPHA_FEATURES_ABILITY,
  ]),
});

export const CUSTOMER_SUPPORT_ABILITY_GROUP = new AbilityGroup({
  id: 'customer-support',
  getName: (t) => t('admin.users.ability-groups.customer-support'),
  abilities: List([
    DO_RTC_CUSTOMER_SUPPORT_ABILITY,
  ]),
});

export const SOCIAL_QUESTION_ABILITY_GROUP = new AbilityGroup({
  id: 'social-question',
  getName: (t) => t('admin.users.ability-groups.social-question'),
  abilities: List([
    EDIT_SOCIAL_QUESTION_ABILITY,
    ANSWER_SOCIAL_QUESTION_ABILITY,
  ]),
});

export const ABILITY_GROUPS = List([
  ADMIN_ABILITY_GROUP,
  MATERIALS_ABILITY_GROUP,
  DICTIONARY_ABILITY_GROUP,
  SITE_CONTENT_ABILITY_GROUP,
  TEACHING_ABILITY_GROUP,
  TESTING_ABILITY_GROUP,
  CUSTOMER_SUPPORT_ABILITY_GROUP,
  SOCIAL_QUESTION_ABILITY_GROUP,
]);

// TODO: break this up to separate sets of who can view the lesson
// editor, who can edit fields on lessons

// Reused/commonly found groups of permissions.
export const EDIT_LESSON_PERMISSIONS = List([
  EDIT_LESSON_ABILITY,
  EDIT_TEMPLATE_ABILITY,
  EDIT_LESSON_TRANSLATION_ABILITY,
]);

export const EDIT_LESSON_TRANSLATION_PERMISSIONS = List([
  EDIT_LESSON_ABILITY,
  EDIT_LESSON_TRANSLATION_ABILITY,
]);

export const EDIT_DICTIONARY_CONTENT_PERMISSIONS = List([
  EDIT_DICTIONARY_CONTENT_ABILITY,
  TRANSLATE_DICTIONARY_CONTENT_ABILITY,
]);

export const VIEW_DICTIONARY_CONTENT_PERMISSIONS = List([
  EDIT_DICTIONARY_CONTENT_ABILITY,
  TRANSLATE_DICTIONARY_CONTENT_ABILITY,
  EDIT_LESSON_ABILITY,
  EDIT_LESSON_TRANSLATION_ABILITY,
]);

export const VIEW_CURRICULA_PERMISSIONS = List([
  EDIT_CATEGORIES_ABILITY,
  EDIT_ORGANIZATIONS_ABILITY,
  VIEW_ORGANIZATIONS_ABILITY,
]);

export const EDIT_COURSE_PERMISSIONS = List([
  EDIT_CATEGORIES_ABILITY,
]);

export const EDIT_COURSE_TRANSLATION_PERMISSIONS = List([
  EDIT_CATEGORIES_ABILITY,
  EDIT_LESSON_TRANSLATION_ABILITY,
]);

export const EDIT_CATEGORY_PERMISSIONS = List([
  EDIT_CATEGORIES_ABILITY,
]);

export const EDIT_CATEGORY_TRANSLATION_PERMISSIONS = List([
  EDIT_CATEGORIES_ABILITY,
  EDIT_LESSON_TRANSLATION_ABILITY,
]);

export const EDIT_TEMPLATE_PERMISSIONS = List([
  EDIT_TEMPLATE_ABILITY,
  EDIT_LESSON_TRANSLATION_ABILITY,
]);

export const VIEW_EDITOR_LESSON_PERMISSIONS = List([
  ...EDIT_LESSON_PERMISSIONS,
  PUBLISH_LESSON_ABILITY,
]);

export const VIEW_COMMON_TEXT_PERMISSIONS = List([
  ...EDIT_LESSON_PERMISSIONS,
  EDIT_COMMON_TEXT_ABILITY,
]);

export const VIEW_DIALOG_SPEAKER_LIST_PERMISSIONS = List([
  ...EDIT_LESSON_PERMISSIONS,
  EDIT_DIALOG_SPEAKER_ABILITY,
]);

export const EDIT_DIALOG_SPEAKER_PERMISSIONS = List([EDIT_DIALOG_SPEAKER_ABILITY]);

export const CREATE_DIALOG_SPEAKER_PERMISSIONS = EDIT_DIALOG_SPEAKER_PERMISSIONS;

export const DELETE_DIALOG_SPEAKER_PERMISSIONS = EDIT_DIALOG_SPEAKER_PERMISSIONS;

export const VIEW_CONTENT_STRUCTURE_PERMISSIONS = List([
  EDIT_LESSON_ABILITY,
  EDIT_TEMPLATE_ABILITY,
  EDIT_LESSON_TRANSLATION_ABILITY,
  EDIT_CATEGORIES_ABILITY,
]);

export const EDIT_SOCIAL_QUESTION_PERMISSIONS = List([
  EDIT_SOCIAL_QUESTION_ABILITY,
]);

export const EDIT_CONTENT_STRUCTURE_PERMISSIONS = List([
  EDIT_CATEGORIES_ABILITY,
]);

export const CREATE_LESSON_PERMISSIONS = List([
  EDIT_TEMPLATE_ABILITY,
  EDIT_LESSON_ABILITY,
]);

export const CREATE_LESSON_TEMPLATE_PERMISSIONS = List([EDIT_TEMPLATE_ABILITY]);

export const USER_MANAGEMENT_PERMISSIONS = List([
  VIEW_USERS_ABILITY,
  EDIT_USERS_ABILITY,
  DELETE_USERS_ABILITY,
]);

export const CREATE_OTHER_USER_PERMISSIONS = List([CREATE_OTHER_USERS_ABILITY]);
export const VIEW_USER_PERMISSIONS = List([VIEW_USERS_ABILITY]);

export const EDIT_BLOCKED_DOMAINS_PERMISSIONS = List([EDIT_BLOCKED_EMAIL_DOMAINS_ABILITY]);

export const STAFF_MANAGEMENT_PERMISSIONS = List([MANAGE_STAFF_ABILITY]);

export const TEACHER_MANAGEMENT_PERMISSIONS = List([VIEW_TEACHERS_ABILITY, EDIT_TEACHERS_ABILITY]);

// Allow to visit CommonTextEditor to CommonText editors and
// translators
export const VISIT_COMMON_TEXT_EDITOR_PERMISSIONS = List([
  EDIT_COMMON_TEXT_ABILITY,
  EDIT_LESSON_TRANSLATION_ABILITY,
]);

export const EDIT_COMMON_TEXT_PERMISSIONS = List([
  EDIT_COMMON_TEXT_ABILITY,
]);

export const REPORT_ISSUE_PERMISSIONS = List([
  REPORT_ISSUE_ABILITY,
]);

export const EDIT_ORGANIZATION_PERMISSIONS = List([
  EDIT_ORGANIZATIONS_ABILITY,
  VIEW_ORGANIZATIONS_ABILITY,
  DELETE_ORGANIZATIONS_ABILITY,
]);

export const EDIT_BANNER_PERMISSIONS = List([EDIT_BANNER_ABILITY]);

export const CREATE_COMMON_TEXT_PERMISSIONS = EDIT_COMMON_TEXT_PERMISSIONS;

export const EDIT_LESSON_BOOKINGS_PERMISSIONS = List([EDIT_LESSON_BOOKINGS_ABILITY]);
export const VIEW_LESSON_BOOKINGS_PERMISSIONS = List([VIEW_LESSON_BOOKINGS_ABILITY]);
export const VIEW_LESSON_BOOKING_TRANSCRIPTS_PERMISSIONS = List([VIEW_LESSON_BOOKING_TRANSCRIPTS_ABILITY]);
export const VIEW_LESSON_BOOKING_RECORDINGS_PERMISSIONS = List([VIEW_LESSON_BOOKING_RECORDINGS_ABILITY]);

export const ADMIN_MEETING_BOOKINGS_PERMISSIONS = List([ADMIN_MEETING_BOOKINGS_ABILITY]);
export const ADMIN_BOT_BOOKINGS_PERMISSIONS = List([ADMIN_BOT_BOOKINGS_ABILITY]);

export const USER_ABILITIES = ABILITIES.filter((ability) => ability.subjects.indexOf(Subject.User) >= 0);
export const TEACHER_ABILITIES = ABILITIES.filter((ability) => ability.subjects.indexOf(Subject.Teacher) >= 0);
export const STAFF_ABILITIES = ABILITIES.filter((ability) => ability.subjects.indexOf(Subject.StaffMember) >= 0);
export const ASSIGNABLE_ABILITIES = ABILITIES.filter((ability) => ability.subjects.length > 0);
export const UNIVERSAL_ABILITIES = ABILITIES.filter((ability) => ability.universal);
export const DEFAULT_SCOPES = ASSIGNABLE_ABILITIES.concat(UNIVERSAL_ABILITIES)
                                                  .filter((ability: Ability) => !ability.privileged)
                                                  .toSet();
